<template>
  <div class="objective-list-panel">
    <div v-if="objectives.length <= 0" class="mt-3">
      <div class="font-icon-detail">
        <span style="font-size: 3em">
          <i class="tim-icons icon-puzzle-10"></i>
        </span>
        <h4 class="mt-3">No objectives have been added.</h4>
      </div>
    </div>

    <div v-else class="objective-list" style="max-height: calc(100vh - 320px); overflow-y: auto;">
      <draggable v-model="topLevelItems" handle=".parent-drag-handle"
                 @end="onDragEnd" :options="{ animation: 200 }">
        <div v-for="(item, index) in topLevelItems" :key="'obj-' + item.id">
          <!-- Parent objective -->
          <div class="objective-list-item"
               :class="{ active: selectedId === item.id }"
               @click="$emit('select', item.id)">
            <div class="d-flex align-items-center flex-grow-1" style="min-width: 0;">
              <i class="tim-icons el-icon-rank parent-drag-handle drag-handle mr-3"
                 style="cursor: move; font-size: 14px;"
                 @click.stop></i>
              <span class="objective-list-item__number">{{ index + 1 }}</span>
              <img v-if="item.solution_img" :src="item.solution_img"
                   class="objective-list-item__img ml-2" />
              <div class="ml-2 flex-grow-1" style="min-width: 0;">
                <span class="objective-list-item__name text-truncate d-block">
                  {{ item.name || 'Untitled objective' }}
                </span>
                <div v-if="item.description" class="objective-list-item__meta text-truncate">
                  {{ item.description }}
                </div>
              </div>
              <el-tooltip content="Add sub-objective" effect="light" :open-delay="300" placement="top">
                <i class="tim-icons el-icon-plus objective-add-child-btn"
                   @click.stop="$emit('create-child', item.id)"></i>
              </el-tooltip>
            </div>
          </div>

          <!-- Child objectives -->
          <draggable v-if="childrenOf[item.id] && childrenOf[item.id].length > 0"
                     :list="childrenOf[item.id]"
                     handle=".child-drag-handle"
                     :options="{ animation: 200 }"
                     @end="onChildDragEnd(item)"
                     class="objective-children">
            <div v-for="(child, ci) in childrenOf[item.id]" :key="'child-' + child.id"
                 class="objective-list-item objective-list-item--child"
                 :class="{ active: selectedId === child.id }"
                 @click.stop="$emit('select', child.id)">
              <div class="d-flex align-items-center">
                <i class="tim-icons el-icon-rank child-drag-handle drag-handle mr-2"
                   style="cursor: move; opacity: 0.6; font-size: 12px;"
                   @click.stop></i>
                <span class="objective-list-item__number">{{ index + 1 }}{{ getLetter(ci) }}</span>
                <img v-if="child.solution_img" :src="child.solution_img"
                     class="objective-list-item__img ml-2" />
                <div class="ml-2 flex-grow-1" style="min-width: 0;">
                  <span class="objective-list-item__name text-truncate d-block">
                    {{ child.name || 'Untitled' }}
                  </span>
                  <div v-if="child.description" class="objective-list-item__meta text-truncate">
                    {{ child.description }}
                  </div>
                </div>
              </div>
            </div>
          </draggable>

        </div>
      </draggable>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { Tooltip } from 'element-ui'

export default {
  name: 'ObjectiveListPanel',
  components: { draggable, [Tooltip.name]: Tooltip },
  props: {
    objectives: { type: Array, default: () => [] },
    selectedId: { type: Number, default: null },
    loading: { type: Boolean, default: false }
  },
  computed: {
    topLevelItems: {
      get() {
        return this.objectives
          .filter(p => !p.parent_id)
          .sort((a, b) => a.position - b.position)
      },
      set(value) {
        const reordered = value.map((item, i) => ({ ...item, position: i }))
        this.$emit('reorder', reordered)
      }
    },
    childrenOf() {
      const map = {}
      for (const parent of this.objectives.filter(p => !p.parent_id)) {
        map[parent.id] = this.objectives
          .filter(p => p.parent_id === parent.id)
          .sort((a, b) => a.position - b.position)
      }
      return map
    }
  },
  methods: {
    getLetter(index) {
      return String.fromCharCode(97 + index)
    },
    onDragEnd() {
      // Handled by the v-model setter on topLevelItems
    },
    onChildDragEnd(parent) {
      const children = this.childrenOf[parent.id]
      if (!children) return
      const reordered = children.map((item, i) => ({ ...item, position: i }))
      this.$emit('reorder', reordered)
    }
  }
}
</script>

<style scoped>
.objective-list-panel {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 8px 0 28px;
}

.objective-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-left: 3px solid transparent;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.objective-list-item:hover:not(.active) {
  background: rgba(255, 255, 255, 0.04);
}

.objective-list-item.active {
  border-left-color: #e14eca;
  background: rgba(225, 78, 202, 0.08);
}

.objective-list-item--child {
  margin-left: 40px;
  border-left: 2px solid rgba(225, 78, 202, 0.2);
}

.objective-list-item--child.active {
  border-left-color: #e14eca;
}

.objective-list-item__number {
  font-size: 12px;
  font-weight: 600;
  color: #e14eca;
  min-width: 20px;
  flex-shrink: 0;
}

.objective-list-item__name {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
}

.objective-list-item__meta {
  font-size: 11px;
  color: #9a9a9a;
  margin-top: 1px;
}

.objective-children {
  /* Structural only - styled via child items */
}

.objective-list-item__img {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
}

.drag-handle {
  color: rgba(255, 255, 255, 0.4);
}

.objective-add-child-btn {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  flex-shrink: 0;
  transition: color 0.15s;
}

.objective-add-child-btn:hover {
  color: #e14eca;
}
</style>

<style>
/* Light mode overrides */
.white-content .objective-list-panel {
  background: #ffffff;
  border-color: #e3e3e3;
}

.white-content .objective-list-item:hover:not(.active) {
  background: #f5f6fa;
}

.white-content .objective-list-item.active {
  background: rgba(225, 78, 202, 0.06);
}

.white-content .objective-list-item--child {
  border-left-color: rgba(225, 78, 202, 0.2);
}

.white-content .objective-list-item__name {
  color: #344675;
}

.white-content .objective-add-child-btn {
  color: #9A9A9A;
}

.white-content .objective-add-child-btn:hover {
  color: #e14eca;
}

.white-content .drag-handle {
  color: rgba(0, 0, 0, 0.3);
}
</style>
